import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { LL, BOOT_LOCALE } from 'i18n/i18n.service';
import { TranslatedString } from 'models/translated-string';
import { DateTime } from 'luxon';
import { BodyCompositionDataChangedEvent, TaskStatusCacheUpdatedEvent, UserDb } from 'services/user-db.service';
import { taskStatusChangeAffectsVisibleDays, TaskStatusChangeRange } from 'services/user-db.task-status';
import { CloudStateChangeEvent, DatabaseState, DatabaseWrapper, DatabaseWrapperProvider, DocWatcher, DayUpdatedEvent } from 'services/database-wrapper';
import { UiDisplayNutrient } from 'models/display-nutrient';
import { DebugService } from 'services/debug.service';
import { LoggingService, isTransientFetchError, isCouchHttpErrorObject, errorReportFields } from 'services/logging.service';
import { isDatabaseRecovering, isDatabaseTornDown, isDatabaseUnavailable } from 'services/database-unavailable-error';
import { isPouchNotFound } from '@cf-shared/data-model/pouch';
import { FitnessPlan, WelcomePhase } from '@cf-shared/data-model/fitness-plan';
import { CsTaskCalendar } from 'components/cs-task-calendar/cs-task-calendar';
import { SvPlanHandlerService } from 'services/server/sv-plan-handler.service';
import { isPlanCheckFailed } from 'services/server/plan-check-failure';
import { AdjustmentActionReason } from '@cf-shared/plan-update-internals';
import { PlanUpdateUserMessage } from '@cf-shared/plan-update-interface';
import { ProgressStatus } from '@cf-shared/body-comp-overview';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { CsPage, NavController } from 'providers/nav-controller';
import { AuthService } from 'services/auth.service';
import { getMyApp } from 'app/app-interface';
import { bootTimings, BootPhase } from 'app/boot-timings';
import { EntryStepRunner, entryStepRunner, pageEntryStepTracker, trackPageEntryStepAsync, untrackedEntryStep } from 'providers/page-lifecycle-guard';
import { formatWeekdayDatePaddedWithYear } from '@cf-shared/date-format';
import { ConfigDbService } from 'services/config-db.service';
import { PubSubRegistration, PubSubService } from 'services/pubsub.service';
import { AppSettingsChangedEvent } from 'pages/settings/app-settings-page';
import { CsFabComponent } from 'components/cs-fab/cs-fab';
import { CsDayItemsComponent } from 'components/cs-day-items/cs-day-items';
import { DeclaredState, FitnessState, ImmutableState, ProfileState, isUsersTrainingGoalGrowth } from '@cf-shared/data-model/user-state';
import { Mutex } from '@cf-shared/mutex';
import { LogRecordType } from '@cf-shared/data-model/user-log';
import { Log_WeighIn } from '@cf-shared/data-model/log-weigh-in';
import { estimateWeightFromWeighIns } from '@cf-shared/weight-trend';
import { roundToPrecision } from '@cf-shared/shared-util';
import { estimateBFfromConstantLBM } from '@cf-shared/plan-update-shared';
import { TelemetryService } from 'services/telemetry.service';
import { FoodDbService } from 'services/food-db.service';
import { TaskStatus } from 'components/cs-task-calendar-day/cs-task-calendar-day';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { DayAnimationQueue } from 'services/day-animation-queue';
import { ActivityMenuService } from 'services/activity-menu.service';
import { PopupMenuProvider, PopupMenuButton } from 'providers/popup-menu/popup-menu';
import { CoachingTipsService, ScheduleTipId, ScheduleWarningsData, scheduleTipText } from 'services/coaching-tips.service';
import { SettingsService } from 'services/settings.service';
import { ITaskCalendarPage } from 'pages/task-calendar-page/task-calendar-interface';
import { IProgressPage } from 'pages/progress/progress-interface';
import { IScheduleTipsPage } from 'pages/schedule/schedule-tips-interface';
import { ProgressChartSeries, ProgressChartDataPoint, ProgressChartAxisConfig, ProgressChartTrend } from 'components/cs-progress-chart/cs-progress-chart';
import { isRunningTrendChartsOnAsync, watchRunningTrendCharts } from 'services/running-trend-flag';
import { ChartDataBodyCompositionService } from 'services/chart-data-body-composition.service';
import { ProgressStatusRule, progressStatus } from 'services/progress-status';
import { isCoachingDashboardOnAsync } from 'services/coaching-dashboard-flag';
import { ChartDataNutritionService } from 'services/chart-data-nutrition.service';
import { MealTunerMeal } from '@cf-shared/meal-tuner-interface';
import { MealPhotoScannerResult } from 'pages/meal-photo-scanner-page/meal-photo-scanner-interface';
import { FoodsPageResult } from 'pages/foods/foods-interface';
import { HomePageParams, HomeSlot, HomeTab } from './home-interface';
import { VersionCheckService } from 'services/version-check.service';
import { AlertButtonRole, AlertController } from 'providers/alert-controller';
import { ICustomizeHomepagePage } from 'pages/customize-homepage/customize-homepage-interface';
import { IEditTrainingGoalPage } from 'pages/edit-training-goal/edit-training-goal-interface';
import { formatRelativeDay, upperFirstTranslated } from 'util/formatting';

export enum GoalType {
  FatLoss = 'fat-loss',
  WeightLoss = 'weight-loss',
  WeightGain = 'weight-gain',
  GrowthMindset = 'growth-mindset'
}

export interface GoalData {
  type: GoalType;
  target: string;
  isCutting: boolean;
  progress?: {
    start: string;
    startValue: number;
    goal: string;
    goalValue: number;
    currentValue: number;
    unit?: string;
  };
  result?: {
    currentValue: string;
    change: string;
    trend: 'up' | 'down' | 'flat';
    trendQuality: 'good' | 'bad' | 'neutral';
  };
  status?: {
    text: string;
    isOnTrack: boolean;
    isIndeterminate: boolean;
    indeterminateMessage?: PlanUpdateUserMessage;
    offTrackReason?: AdjustmentActionReason;
    recentAdjustmentDaysSince?: number;
    daysUntilResume?: number;
    isPlanBaseline?: boolean;
  };
}

// which of the two day copies the page is talking about. The page draws the day
// twice so it can slide one out while the next comes in. This is narrower than
// HomeSlot, which also names the Progress tab.
type DaySlotId = 'A' | 'B';

// everything one day copy holds. Two of these instead of a lettered pair of
// fields per value, so anything new a day slot has to hold is one field here
// rather than two fields plus a two-branch write at every place that sets it.
//
// The day's nutrients are deliberately NOT in here: the Home page shows them in
// the shared header rather than inside one day copy, so it keeps them in the
// single dayDisplayNutrients field. The sibling Nutrition page differs there.
interface HomeDaySlot {
  loading: boolean;
}

// a day slot as it starts out: not loading
function createEmptyDaySlot(): HomeDaySlot {
  return { loading: false };
}

@Component({
  selector: 'page-home',
  templateUrl: 'home.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomePage extends CsPage<HomePageParams> implements AfterViewInit, OnDestroy {
  // Messages whose root cause can be a recent nutrition-adjustment-driven reset of
  // the 14-day stats window — for these, the "Nutrition adjusted N days ago, assessment
  // resumes in ~M days" banner is more useful than the message's own copy. Other
  // messages (DataErratic, WeightTooFlat) point at specific data problems and pass
  // through this banner unchanged. See getRecentAdjustmentBanner below.
  private static readonly RECENT_ADJUSTMENT_MESSAGES: ReadonlySet<PlanUpdateUserMessage> = new Set([
    PlanUpdateUserMessage.NotEnoughData,
    PlanUpdateUserMessage.MeasureMoreOften,
    PlanUpdateUserMessage.InsufficientFatnessConfidence,
  ]);

  protected LL = LL;
  @ViewChild('dayItemsA') dayItemsA: CsDayItemsComponent;
  @ViewChild('dayItemsB') dayItemsB: CsDayItemsComponent;
  @ViewChild('addFab') addFabComponent: CsFabComponent;
  @ViewChild(CsTaskCalendar) taskCalendar: CsTaskCalendar;

  protected animateTabContent = false;
  // unified slot system - combines day slots (A/B) with tabs (DailyLog/Progress)
  protected activeSlot: HomeSlot = 'daylog-A';
  protected isBack: boolean = false;
  // track which day slot was last active (for returning from Progress tab)
  private lastDaySlot: DaySlotId = 'A';
  // queue for rapid day changes - prevents animation/data load interruption
  private dayQueue = new DayAnimationQueue();
  // the two day copies, one record each. The template reads them by letter
  // because it draws both slots; the code below reaches them through the slot
  // it is already holding.
  protected daySlots: Record<DaySlotId, HomeDaySlot> = {
    A: createEmptyDaySlot(),
    B: createEmptyDaySlot(),
  };
  plan: FitnessPlan;
  fitnessState: FitnessState;
  profile: ProfileState;
  declaredState: DeclaredState;
  immutableState: ImmutableState;
  fakeToday: string = '';
  currentDay: DateTime;
  currentDayLabel: TranslatedString;
  currentWeekday: string = ''; // this is needed for E2E tests
  cloudSyncState: DatabaseState = undefined;
  dayDisplayNutrients: UiDisplayNutrient[];
  awaitingForBodyfatEstimate = false;
  calendarTaskStatuses: Map<string, TaskStatus> = new Map();

  // derived: current day slot (A or B)
  get activeDaySlot(): DaySlotId {
    if (this.activeSlot === 'daylog-A') return 'A';
    if (this.activeSlot === 'daylog-B') return 'B';
    return this.lastDaySlot;
  }

  // derived: selectedTab for cs-tab component
  get selectedTab(): HomeTab {
    return this.activeSlot === 'progress' ? HomeTab.Progress : HomeTab.DailyLog;
  }

  // setter intercepts cs-tab changes to trigger slot animations
  set selectedTab(value: HomeTab) {
    if (value === this.selectedTab) return;

    if (value === HomeTab.Progress) {
      // Daily Log → Progress = forward (Progress is to the right)
      this.isBack = false;
      this.activeSlot = 'progress';
    } else {
      // Progress → Daily Log = back (Daily Log is to the left)
      this.isBack = true;
      this.activeSlot = this.lastDaySlot === 'A' ? 'daylog-A' : 'daylog-B';
    }
    this.cd.markForCheck();
  }
  HomeTab = HomeTab;
  GoalType = GoalType;
  AdjustmentActionReason = AdjustmentActionReason;
  PlanUpdateUserMessage = PlanUpdateUserMessage;
  goalData: GoalData;
  // the schedule tips shown on the coaching tips card, by id; the template looks up their text
  coachingTips: ScheduleTipId[] = [];
  // if the tips were built and none applies, or they could not be built, the card shows a positive message instead
  showNoTipsMessage = false;
  scheduleWarnings: ScheduleWarningsData = null;
  protected scheduleTipText = scheduleTipText;

  // getter for progress bar width percentage (clamped to 0-100)
  get goalProgressPercent(): number {
    if (!this.goalData?.progress) return 0;
    const { startValue, currentValue, goalValue } = this.goalData.progress;
    const percent = ((startValue - currentValue) / (startValue - goalValue)) * 100;
    return Math.max(0, Math.min(100, percent));
  }

  // if the indeterminate state is caused by a recent adjustment, pick the right
  // i18n variant (baseline vs adjustment, today vs N-days-ago); returns null when
  // the legacy message branches should be shown instead
  get recentAdjustmentBannerText(): string | null {
    const s = this.goalData?.status;
    if (s?.recentAdjustmentDaysSince == null) return null;
    const N1 = s.recentAdjustmentDaysSince;
    const N2 = s.daysUntilResume;
    if (s.isPlanBaseline) {
      return N1 === 0
        ? LL.home.progress.goalCard.planJustStartedToday({ N2 })
        : LL.home.progress.goalCard.planJustStarted({ N1, N2 });
    }
    return N1 === 0
      ? LL.home.progress.goalCard.nutritionAdjustedToday({ N2 })
      : LL.home.progress.goalCard.nutritionAdjusted({ N1, N2 });
  }

  // Body composition chart properties
  bodyCompositionStartDate: string = '';
  bodyCompositionEndDate: string = '';
  bodyCompositionSeries: ProgressChartSeries[] = [];
  bodyCompositionData: ProgressChartDataPoint[] = [];
  bodyCompositionAxisConfig: ProgressChartAxisConfig = { stacked: true };
  /** the running trend line on the small chart (#6627), only while its switch is on; no tunnel here */
  bodyCompositionTrends: ProgressChartTrend[] = [];
  /** counts trend loads, so an older answer never replaces a newer one */
  private bodyCompositionTrendLoadCounter = 0;
  private unwatchRunningTrend?: () => void;

  // Nutrition chart properties
  nutritionStartDate: string = '';
  nutritionEndDate: string = '';
  nutritionSeries: ProgressChartSeries[] = [];
  nutritionData: ProgressChartDataPoint[] = [];
  nutritionAxisConfig: ProgressChartAxisConfig = { stacked: false };

  private docWatcher: DocWatcher;

  DatabaseState = DatabaseState;
  settingsSub: PubSubRegistration;
  cloudStateSub: PubSubRegistration;
  dayUpdatedSub: PubSubRegistration;
  taskStatusCacheUpdatedSub: PubSubRegistration;
  bodyCompositionDataChangedSub: PubSubRegistration;

  // true while Home is the page on screen: set on enter, cleared on leave. The
  // "Please update" alert (#6770) opens only while this is still true when its slow
  // status answer arrives, so it never covers a page pushed meanwhile.
  private homeIsActive = false;

  constructor(
    public navCtrl: NavController,
    private userDb: UserDb,
    private pubSub: PubSubService,
    private debug: DebugService,
    public svPlanHandler: SvPlanHandlerService,
    public cloudFunctionsService: CloudFunctionsService,
    private dbProvider: DatabaseWrapperProvider,
    protected auth: AuthService,
    public configDb: ConfigDbService,
    private telemetry: TelemetryService,
    public foodDb: FoodDbService,
    private cd: ChangeDetectorRef,
    
    private activityMenuService: ActivityMenuService,
    private popupMenuProvider: PopupMenuProvider,
    private coachingTipsService: CoachingTipsService,
    public settings: SettingsService,
    private elementRef: ElementRef,
    protected bodyCompDataService: ChartDataBodyCompositionService,
    protected nutritionDataService: ChartDataNutritionService,
    private logging: LoggingService,
    private versionCheck: VersionCheckService,
    private alertCtrl: AlertController,
  ) {
    super();
  }

  ngAfterViewInit() {
    // enable tab content animation after initial render
    requestAnimationFrame(() => {
      if (this.viewDestroyed) return;
      this.animateTabContent = true;
      this.cd.markForCheck();
    });
  }

  async ionViewCanEnter() {
    let isoDateLocal = this.navParams.data.day;
    if (isoDateLocal) {
      this.currentDay = DateTime.fromISO(isoDateLocal).startOf('day');
    }
    if (!this.currentDay || !this.currentDay.isValid) {
      this.currentDay = this.debug.getNowLocal().startOf('day');
    }

    // handle initial tab from URL
    const initialTab = this.navParams.data.tab;
    if (initialTab === HomeTab.Progress) {
      this.activeSlot = 'progress';
    }
    // default is already 'daylog-A', no action needed for DailyLog

    // re-save cached log entries to database for today or past days
    void this.userDb.resaveCachedEntriesForDayAsync(this.currentDay);
  }

  shouldShowAddButton() {
    return true;
  }

  shouldShowAddWorkoutButton() {
    const scheduledItems = this.getScheduledItems();
    if (!scheduledItems) {
      return false;
    }
    const workoutItem = scheduledItems.find(item => item.entry.type === LogRecordType.Workout);
    if (!workoutItem) {
      return true;
    }
    return false;
  }

  hasClientSmallScreen = hasClientSmallScreen;

  // Check if all daily log items are hidden via homepage customization
  get allDailyLogItemsHidden(): boolean {
    return !this.settings.showDayStartEvents &&
           !this.settings.homepageShowWeighIn &&
           !this.settings.homepageShowWorkout &&
           !this.settings.homepageShowMeals &&
           !this.settings.showDayEndEvents;
  }

  // Check if all progress items are hidden via homepage customization
  get allProgressItemsHidden(): boolean {
    return !this.settings.homepageShowGoalCard &&
           !this.settings.homepageShowBodyComposition &&
           !this.settings.homepageShowNutritionChart &&
           !this.settings.homepageShowCoachingTips;
  }

  async handleCustomizeClick() {
    await this.navCtrl.pushAsync(ICustomizeHomepagePage, {});
  }

  async handleLogActivityClick(event: Event) {
    const target = event.currentTarget as HTMLElement;
    const scheduledItems = this.getScheduledItems();
    const logEntries = scheduledItems.map(item => item.entry);
    await this.activityMenuService.showActivityMenu(target, this.docWatcher, this.currentDay, logEntries);
  }

  async onTitleClick() {
    const result = await this.navCtrl.callAsync(ITaskCalendarPage, {
      selectedDateISO: this.currentDay.toISO(),
      title: 'Select Date',
      local: {
        taskStatusLoader: (startDate, endDate) => this.loadTaskStatusesForRange(startDate, endDate),
      },
    });

    if (result?.selectedDate) {
      void this.gotoDateAsync(result.selectedDate.startOf('day'));
    }
  }

  isAtToday() {
    let now = this.debug.getNowLocal();
    return this.currentDay.hasSame(now, 'day');
  }

  async ionViewWillEnter() {
    // O1 (#3986): time the FIRST home render of the boot window only.
    // startOnce is a no-op once the phase is measured (or still open), so
    // later re-entries cannot overwrite the boot's first-render number; a
    // re-boot that calls bootTimings.reset() re-arms it. If a first render
    // throws mid-way, the open measurement is closed by the next re-entry's
    // end() below — a longer-than-real number in that rare error path beats
    // never measuring the phase again.
    bootTimings.startOnce(BootPhase.FirstHomeRender);

    // #4537: every await below is wrapped in trackPageEntryStepAsync, so that if this
    // method runs out of time the PageWillEnterTimeout report can say WHICH step had not
    // come back. It is report-only — the wrapper changes nothing about what runs, in what
    // order, or what it returns. Why this page: cs-nav bounds this hook at ten seconds and
    // names the page that missed it, and reading three weeks of that report showed it is
    // almost always this one. See design-docs/boot-sequence.md, "What the #4413 report
    // says, three weeks on", for the figures and what is still unknown.
    //
    // ADD THE WRAPPER TO ANY NEW await IN THIS METHOD. A bare one is invisible to the
    // report: if it is the slow step the report names no step at all, which reads as "this
    // page is not instrumented" rather than as the answer. A test pins that every await in
    // this method goes through the wrapper, so a bare one fails rather than going quiet.

    // skip reload when returning from a modal - the callback will handle any date changes
    // (e.g., onTaskCalendarResult calls gotoDateAsync if a different date was selected)
    const pageElement = this.elementRef.nativeElement as HTMLElement;
    const wasUnderModal = pageElement.classList.contains('previous-visible');
    // #4537: the step tracker is shared by every page, so each step records who opened it.
    // Read from the element rather than hard-coded, for the same reason cs-nav reads the
    // timing-out page's name from ITS element: the two have to agree, and a literal here
    // would silently stop matching if the selector ever changed.
    const entryPage = pageElement.tagName.toLowerCase();
    // #4988: read once, here — this visit can outlast the next entry of this page, and its
    // later steps must not be counted as that entry's
    const entryVisit = pageEntryStepTracker.currentEntry(entryPage);

    // lazy-init DocWatcher so synced document changes trigger a reactive reload
    if (!this.docWatcher) {
      this.docWatcher = this.userDb.database.createDocWatcher((_changes) => {
        if (this.viewDestroyed) return undefined;
        this.cd.markForCheck();
        // #4358: RETURN it, never `void` it — this reload reads fourteen days of
        // log entries, and fire-and-forget here is what froze the app. See the
        // DocWatcher delivery contract in design-docs/reactive-sync.md.
        return this.loadGoalDataAsync();
      }, { label: 'home-page' });
    }

    if (!wasUnderModal) {
      // #4110: never let a failed day load abort ionViewWillEnter — the subscriptions
      // registered below (AppSettingsChangedEvent, DayUpdatedEvent, ...) each re-run
      // loadCurrentDayAsync and are exactly what recovers the page after a failure.
      // before this catch, the very failure that emptied the page also skipped them.
      try {
        await trackPageEntryStepAsync(pageEntryStepTracker, entryPage, 'loadCurrentDay', () => this.loadCurrentDayAsync(entryStepRunner(pageEntryStepTracker, entryPage, entryVisit)), entryVisit);
      } catch (err) {
        if (isTransientFetchError(err)) {
          // cs-day-items handles its own transient read failures (deferred reload), so a
          // transient error here comes from a sibling load — report it and keep going
          this.logging.trackSilentProblem('Home: day load failed on enter (transient network)', errorReportFields(err));
        } else {
          this.logging.trackAbnormalException(err);
        }
      }
      void this.loadCalendarTaskStatuses();
      void this.updateCoachingTips();

      // fetch chart data (don't await, let it load in background)
      void this.fetchChartsDataAsync();
    }

    this.plan = await trackPageEntryStepAsync(pageEntryStepTracker, entryPage, 'loadPlan', () => this.userDb.loadPlanAsync(this.docWatcher), entryVisit);
    this.fitnessState = await trackPageEntryStepAsync(pageEntryStepTracker, entryPage, 'loadFitnessState', () => this.userDb.loadFitnessStateAsync(this.docWatcher), entryVisit);
    this.profile = await trackPageEntryStepAsync(pageEntryStepTracker, entryPage, 'loadProfileState', () => this.userDb.loadProfileStateAsync(this.docWatcher), entryVisit);
    this.declaredState = await trackPageEntryStepAsync(pageEntryStepTracker, entryPage, 'loadDeclaredState', () => this.userDb.loadDeclaredStateAsync(this.docWatcher), entryVisit);
    this.immutableState = await trackPageEntryStepAsync(pageEntryStepTracker, entryPage, 'loadImmutableState', () => this.userDb.loadImmutableStateAsync(this.docWatcher), entryVisit);

    // Load real goal data
    await trackPageEntryStepAsync(pageEntryStepTracker, entryPage, 'loadGoalData', () => this.loadGoalDataAsync(), entryVisit);

    this.cloudStateSub = this.pubSub.subscribe(CloudStateChangeEvent, (data: { state: DatabaseState }) => {
      this.updateCloudStatus(data.state);
    });

    this.settingsSub = this.pubSub.subscribe(AppSettingsChangedEvent, () => {
      void this.loadCurrentDayAsync();
    });

    // Only subscribe to DayUpdatedEvent if not already subscribed
    if (!this.dayUpdatedSub) {
      this.dayUpdatedSub = this.pubSub.subscribe(DayUpdatedEvent, () => {
        void this.loadCurrentDayAsync();
      });
    }

    // Subscribe to task status cache updates
    this.taskStatusCacheUpdatedSub = this.pubSub.subscribe(TaskStatusCacheUpdatedEvent, (range) => {
      void this.loadCalendarTaskStatuses(range);
    });

    // Only subscribe to BodyCompositionDataChangedEvent if not already subscribed
    if (!this.bodyCompositionDataChangedSub) {
      this.bodyCompositionDataChangedSub = this.pubSub.subscribe(BodyCompositionDataChangedEvent, () => {
        void this.refreshBodyCompositionDataAsync();
      });
    }
    // the trend line appears or goes as soon as its switch is flipped, without leaving the page
    if (!this.unwatchRunningTrend) {
      this.unwatchRunningTrend = watchRunningTrendCharts(this.configDb, () => void this.loadBodyCompositionTrendsAsync());
    }

    await trackPageEntryStepAsync(pageEntryStepTracker, entryPage, 'completeWelcomeFlow', () => this.completeWelcomeFlow(), entryVisit);

    // no-op unless a first-render measurement is still open (see startOnce above)
    bootTimings.end(BootPhase.FirstHomeRender);
  }

  async ionViewDidEnter(): Promise<void> {
    this.homeIsActive = true;
    // fire-and-forget: the check can wait up to 30 s for the platform's answer, and
    // Home must not wait for it
    this.versionCheck.checkRequiredPromptAsync({
      isHomeActive: () => this.homeIsActive && !this.viewDestroyed,
      waitNavigationSettledAsync: () => this.navCtrl.whenNavigationInitializedAsync(),
      openAlert: availableVersion => {
        this.showUpdateRequiredAlertAsync(availableVersion).catch(err => this.logging.trackAbnormalException(err));
      },
    }).catch(err => this.logging.trackAbnormalException(err));
  }

  // Shows the "Please update" alert (#6770) and acts on its answer. The answer
  // arrives once the alert is gone, so the update starts with Home underneath: a
  // restart or reload it triggers lands here. Any answer other than 'update'
  // (Later, the back button, a backdrop click) counts as Later.
  private async showUpdateRequiredAlertAsync(availableVersion: string): Promise<void> {
    const belowMinimum = this.versionCheck.isBelowMinimum();
    this.telemetry.trackAppUpdateEvent('shown', 'required_alert', availableVersion, belowMinimum);
    const answer = await this.alertCtrl.showAlertAsync<'update' | 'later'>({
      title: LL.app.updateRequired.title(),
      message: LL.app.updateRequired.message(),
      buttons: [
        { text: LL.app.updateRequired.update(), role: AlertButtonRole.Primary, result: 'update' },
        { text: LL.app.updateRequired.later(), role: AlertButtonRole.Cancel, result: 'later' },
      ],
      implicitCancelResult: 'later',
    });
    if (answer !== 'update') {
      this.telemetry.trackAppUpdateEvent('dismissed', 'required_alert', availableVersion, belowMinimum);
      return;
    }
    this.telemetry.trackAppUpdateEvent('clicked', 'required_alert', availableVersion, belowMinimum);
    await this.versionCheck.startRequiredUpdateAsync();
  }

  async ionViewWillLeave() {
    this.homeIsActive = false;
    this.debug.setPageDebug(null);
    if (this.settingsSub) {
      this.settingsSub.unsubscribe();
    }
    if (this.cloudStateSub) {
      this.cloudStateSub.unsubscribe();
    }
    if (this.taskStatusCacheUpdatedSub) {
      this.taskStatusCacheUpdatedSub.unsubscribe();
    }
    // clear debounce timer
    if (this.debounceCloudIconTimer !== undefined) {
      clearTimeout(this.debounceCloudIconTimer);
      this.debounceCloudIconTimer = undefined;
    }
    // dayUpdatedSub and bodyCompositionDataChangedSub are intentionally kept alive so the
    // home page reacts to updates from pushed pages (e.g. edit-weigh-in, weekly-checkup).
    // They are cleaned up in ngOnDestroy when the instance is destroyed.
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();

    // clean up subscriptions that survive ionViewWillLeave
    if (this.dayUpdatedSub) {
      this.dayUpdatedSub.unsubscribe();
      this.dayUpdatedSub = undefined;
    }
    if (this.bodyCompositionDataChangedSub) {
      this.bodyCompositionDataChangedSub.unsubscribe();
      this.bodyCompositionDataChangedSub = undefined;
    }
    this.unwatchRunningTrend?.();
    this.unwatchRunningTrend = undefined;
  }

  /// set state after the delay - unless another state overwrites it
  debounceCloudIconTimer: ReturnType<typeof setTimeout>;
  debounceCloudIcon(newState: DatabaseState, time_ms: number) {
    let setState = () => {
      if (this.viewDestroyed) return;
      this.cloudSyncState = newState;
      this.cd.markForCheck();
    };

    // don't delay initial state
    if (!this.cloudSyncState) {
      setState();
      return;
    }

    if (this.debounceCloudIconTimer !== undefined) {
      clearTimeout(this.debounceCloudIconTimer);
      this.debounceCloudIconTimer = undefined;
    }

    if (time_ms === 0) {
      setState();
    } else {
      this.debounceCloudIconTimer = setTimeout(() => {
        setState();

        clearTimeout(this.debounceCloudIconTimer);
        this.debounceCloudIconTimer = undefined;
      }, time_ms);
    }
  }

  updateCloudStatus(state: DatabaseState) {
    /*
    When starting to sync, we debounce by 0, but add some timeout when setting other states. This
    assures that intermittent on/off that happens during syncing appears as a continual animation.
    */
    if (state === DatabaseState.Offline) {
      this.debounceCloudIcon(state, 1000);
    } else if (state === DatabaseState.Syncing) {
      this.debounceCloudIcon(state, 0);
    } else if (state === DatabaseState.Ready) {
      this.debounceCloudIcon(state, 2000);
    }
  }

  loadingDayLock = new Mutex();

  // `steps` times each part of the load on its own. Only ionViewWillEnter passes a real one
  // (#4988: the field data says this load holds the home page's ten seconds, and the report
  // must say which part); every other caller leaves it untracked.
  async loadCurrentDayAsync(steps: EntryStepRunner = untrackedEntryStep): Promise<void> {
    // set loading on active slot
    this.daySlots[this.activeDaySlot].loading = true;
    this.cd.markForCheck();

    let now = this.debug.getNowLocal();
    // if not faking today
    if (now.hasSame(DateTime.local(), 'day')) {
      this.fakeToday = '';
      // if faking today
    } else {
      this.fakeToday =
        '[' +
        formatWeekdayDatePaddedWithYear(now, BOOT_LOCALE) +
        ']';
    }
    this.currentDayLabel = upperFirstTranslated(formatRelativeDay(now, this.currentDay));
    this.currentWeekday = this.currentDay.weekday.toFixed(0);

    getMyApp().setPageTitle(this.currentDayLabel);
    this.cd.markForCheck();

    // time the wait for the lock as its own part: another load of the day still holding it
    // would otherwise be counted as this load's reads
    let lockAcquired: () => void = () => {};
    const lockWait = steps('day.lockWait', () => new Promise<void>(resolve => { lockAcquired = resolve; }));
    try {
      await this.loadingDayLock.exclusiveCall(async () => {
        lockAcquired();
        await lockWait;
        // Create a new array reference to trigger OnPush change detection in child components
        // Initialize with empty array to prevent template from seeing undefined/null during async operations
        this.dayDisplayNutrients = [];
        try {
          this.updateCloudStatus(this.dbProvider.userDatabasesStatus());

          await this.dayItemsComponent.loadScheduleAsync('all', now, this.currentDay, this.dayDisplayNutrients, steps);
          if (this.viewDestroyed) return;
          this.safeMarkForCheck(this.cd);

        } finally {
          if (this.loadingDayLock.getCount() == 1) {
            // clear loading on active slot
            this.daySlots[this.activeDaySlot].loading = false;
            this.safeMarkForCheck(this.cd);
          }
        }
      });
    } finally {
      // if the lock call threw before running the callback, close the wait anyway
      lockAcquired();
    }
  }

  // the day-items component belonging to a slot. The two components stay
  // separate fields because an Angular view query binds to a named field, so
  // this is the one place that still picks between them by letter.
  private dayItemsFor(slotId: DaySlotId): CsDayItemsComponent {
    return slotId === 'A' ? this.dayItemsA : this.dayItemsB;
  }

  // Get active day items component
  get dayItemsComponent(): CsDayItemsComponent {
    return this.dayItemsFor(this.activeDaySlot);
  }

  getScheduledItems() {
    return this.dayItemsComponent ? this.dayItemsComponent.getScheduledItems() : [];
  }

  // the scheduled items of one named slot. The template calls this for both
  // slots, because it draws both of them.
  getScheduledItemsFor(slotId: DaySlotId) {
    const dayItems = this.dayItemsFor(slotId);
    return dayItems ? dayItems.getScheduledItems() : [];
  }

  async gotoDateAsync(date: DateTime) {
    // if animation/loading is in progress, queue this date and return
    if (!this.dayQueue.tryStart(date)) {
      return;
    }

    let queued: DateTime | null = null;
    try {
      // toggle to next day slot
      const nextDaySlot: DaySlotId = this.activeDaySlot === 'A' ? 'B' : 'A';
      const nextSlot: HomeSlot = nextDaySlot === 'A' ? 'daylog-A' : 'daylog-B';
      const nextDayItems = this.dayItemsFor(nextDaySlot);

      // set direction based on date comparison
      this.isBack = date < this.currentDay;

      // update state - container handles animation automatically
      this.lastDaySlot = nextDaySlot;
      this.currentDay = date.startOf('day');
      this.activeSlot = nextSlot;

      // set loading on next slot
      this.daySlots[nextDaySlot].loading = true;
      this.cd.markForCheck();

      // update URL params - always switch to Daily Log tab when changing date
      await this.navCtrl.updateCurrentPageParamsAsync({ day: date.toISODate(), tab: HomeTab.DailyLog });

      // update day label
      const now = this.debug.getNowLocal();
      this.currentDayLabel = upperFirstTranslated(formatRelativeDay(now, this.currentDay));
      this.currentWeekday = this.currentDay.weekday.toFixed(0);
      getMyApp().setPageTitle(this.currentDayLabel);
      this.safeMarkForCheck(this.cd);

      // re-save cached log entries to database for today or past days
      void this.userDb.resaveCachedEntriesForDayAsync(this.currentDay);

      // load into the next (now active) slot
      this.dayDisplayNutrients = [];
      await nextDayItems.loadScheduleAsync('all', now, this.currentDay, this.dayDisplayNutrients);
      if (this.viewDestroyed) return;

      // clear loading on next slot
      this.daySlots[nextDaySlot].loading = false;
      this.safeMarkForCheck(this.cd);
    } finally {
      // always complete so a thrown await doesn't leave dayQueue stuck and
      // silently drop every subsequent date click.
      queued = this.dayQueue.complete();
    }

    // process any queued date outside the finally so a thrown error still
    // surfaces (the throw resumes after finally and skips this re-dispatch).
    // skip re-dispatch if the view has been destroyed — the re-entered call
    // would mutate state and call markForCheck against a torn-down view.
    if (queued && !this.viewDestroyed) {
      void this.gotoDateAsync(queued);
    }
  }

  handleDayAfter() {
    let newDate = this.currentDay.plus({ days: 1 });
    void this.gotoDateAsync(newDate);
  }

  handleDayBefore() {
    let newDate = this.currentDay.minus({ days: 1 });
    void this.gotoDateAsync(newDate);
  }

  handleSwipeForward() {
    // if on daily log, swipe left = next day
    if (this.activeSlot !== 'progress') {
      this.handleDayAfter();
    }
  }

  handleSwipeBack() {
    if (this.activeSlot === 'progress') {
      // if on progress tab, swipe right = back to daily log
      this.selectedTab = HomeTab.DailyLog;
    } else {
      // if on daily log, swipe right = previous day
      this.handleDayBefore();
    }
  }

  handleDayToday() {
    // only navigate to today if not already on today
    if (!this.isAtToday()) {
      let newDate = this.debug.getNowLocal();
      void this.gotoDateAsync(newDate);
    } else if (this.activeSlot === 'progress') {
      // if already on today but on Progress tab, switch to Daily Log tab
      this.selectedTab = HomeTab.DailyLog;
      void this.navCtrl.updateCurrentPageParamsAsync({
        day: this.currentDay.toISODate(),
        tab: HomeTab.DailyLog
      });
    }
  }

  // Handler for edit goal button click
  async handleEditGoalButtonClick(event: MouseEvent) {
    event.stopPropagation();
    const result = await this.navCtrl.pushAsync(IEditTrainingGoalPage, {});
    if (result?.modified) {
      // reload data after goal changes
      this.declaredState = await this.userDb.loadDeclaredStateAsync(this.docWatcher);
      await this.loadGoalDataAsync();
    }
  }

  // Handler for body composition chart click
  async handleBodyCompositionChartClick() {
    await this.navCtrl.pushAsync(IProgressPage, {
      chartType: 'bodyComposition',
      timePeriod: await this.progressPagePeriodAsync(),
    });
  }

  // which period the Progress page is opened on: 2 weeks while the coaching dashboard is on
  // (#5456), Month otherwise, as before
  private async progressPagePeriodAsync(): Promise<'2weeks' | 'month'> {
    return await isCoachingDashboardOnAsync(this.configDb) ? '2weeks' : 'month';
  }

  // Handler for nutrition chart click
  async handleNutritionChartClick() {
    await this.navCtrl.pushAsync(IProgressPage, {
      chartType: 'nutrition',
      timePeriod: await this.progressPagePeriodAsync(),
    });
  }

  // Load real goal data based on user state
  private async loadGoalDataAsync() {
    if (!this.declaredState || !this.immutableState || !this.fitnessState || !this.plan) {
      return;
    }

    const goalType = this.determineGoalType(this.declaredState, this.immutableState, this.fitnessState);

    // determine target display text
    let target: string;
    switch (goalType) {
      case GoalType.FatLoss:
        target = LL.home.progress.goalCard.fatLoss();
        break;
      case GoalType.WeightLoss:
        target = LL.home.progress.goalCard.weightLoss();
        break;
      case GoalType.WeightGain:
        target = LL.home.progress.goalCard.weightGain();
        break;
      case GoalType.GrowthMindset:
        target = LL.home.progress.goalCard.growthMindset();
        break;
    }

    // determine if this is a cutting goal
    const isCutting = this.isCuttingGoal(goalType);

    this.goalData = {
      type: goalType,
      target: target,
      isCutting: isCutting
    };

    // add progress and result for fixed target goals
    if (goalType === GoalType.FatLoss || goalType === GoalType.WeightLoss || goalType === GoalType.WeightGain) {
      await this.addProgressAndResultToGoalDataAsync(goalType);
    }

    // for GrowthMindset, check on-track status using weekly checkup logic
    if (goalType === GoalType.GrowthMindset) {
      await this.addOnTrackStatusToGoalData();
    }

    this.safeMarkForCheck(this.cd);
  }

  // Add progress and result data for goals with fixed targets
  // Uses Theil-Sen estimation for current weight to smooth out daily fluctuations
  private async addProgressAndResultToGoalDataAsync(goalType: GoalType) {
    const startPoint = this.plan.startPoint;
    if (!startPoint) {
      return; // no start point yet
    }

    // get Theil-Sen estimated weight (falls back to fitnessState if not enough data)
    const estimatedWeight = await this.getEstimatedWeightAsync();

    if (goalType === GoalType.FatLoss) {
      // bodyfat percentage goal - use estimated BF based on weight trend if available
      const startBF = startPoint.BF_pc;
      const currentBF = estimatedWeight
        ? this.getEstimatedBodyFat(estimatedWeight)
        : this.fitnessState.bfData?.bf_pc;
      const targetBF = this.declaredState.targetBodyfat_pc;

      if (startBF !== undefined && currentBF !== undefined && targetBF !== undefined) {
        this.goalData.progress = {
          start: `${startBF.toFixed(1)}%`,
          startValue: startBF,
          goal: `${targetBF.toFixed(1)}%`,
          goalValue: targetBF,
          currentValue: currentBF,
          unit: '%'
        };

        const change = currentBF - startBF;
        const trend = change < 0 ? 'down' : change > 0 ? 'up' : 'flat';
        this.goalData.result = {
          currentValue: `${currentBF.toFixed(1)}%`,
          change: `(${change >= 0 ? '+' : ''}${change.toFixed(1)})`,
          trend: trend,
          trendQuality: trend === 'down' ? 'good' : trend === 'up' ? 'bad' : 'neutral'
        };
      }
    } else if (goalType === GoalType.WeightLoss || goalType === GoalType.WeightGain) {
      // body weight goal - use estimated weight if available
      const startWeight = startPoint.BW_kg;
      const currentWeight = estimatedWeight ?? this.fitnessState.weight_kg;
      const targetWeight = this.declaredState.targetBodyWeight_kg;

      if (startWeight !== undefined && currentWeight !== undefined && targetWeight !== undefined) {
        this.goalData.progress = {
          start: `${startWeight.toFixed(1)} kg`,
          startValue: startWeight,
          goal: `${targetWeight.toFixed(1)} kg`,
          goalValue: targetWeight,
          currentValue: currentWeight,
          unit: 'kg'
        };

        const change = currentWeight - startWeight;
        const trend = change > 0 ? 'up' : change < 0 ? 'down' : 'flat';
        let trendQuality: 'good' | 'bad' | 'neutral';
        if (trend === 'flat') {
          trendQuality = 'neutral';
        } else if (goalType === GoalType.WeightGain) {
          trendQuality = trend === 'up' ? 'good' : 'bad';
        } else {
          // WeightLoss
          trendQuality = trend === 'down' ? 'good' : 'bad';
        }
        this.goalData.result = {
          currentValue: `${currentWeight.toFixed(1)} kg`,
          change: `(${change >= 0 ? '+' : ''}${change.toFixed(1)})`,
          trend: trend,
          trendQuality: trendQuality
        };
      }
    }
  }

  /**
   * Calculate Theil-Sen estimated weight from last 14 days of weigh-ins.
   * Uses robust trend estimation to smooth out daily fluctuations.
   * Returns the estimated weight 7 days ago on the trend line (same as plan-update.ts).
   * Returns undefined below the plan engine's weigh-in minimum (#6015), or when no
   * weigh-in reaches back 7 days (#6050), so the goal card falls back to the stored
   * weight / body fat instead of an extrapolated one.
   */
  private async getEstimatedWeightAsync(): Promise<number | undefined> {
    const now = this.debug.getNowLocal();
    const startDate = now.minus({ days: 13 }).startOf('day'); // 14 days of data

    // get weigh-in entries for the period
    const logEntries = await this.userDb.getLogEntriesInRangeAsync(startDate, now.endOf('day'), this.docWatcher, { inLogEntriesTimeZone: true });
    const weighIns = logEntries.filter(e => e.type === LogRecordType.WeighIn) as Log_WeighIn[];

    return estimateWeightFromWeighIns(weighIns, now);
  }

  /**
   * Calculate estimated body fat using constant LBM assumption.
   * Uses the most recent BF measurement as reference point for LBM calculation.
   * When weight changes, BF is estimated proportionally assuming LBM stays constant.
   *
   * The arithmetic itself lives in estimateBFfromConstantLBM in shared/src/plan-update-shared.ts,
   * which is also called by estimateBFandLBM in that same file — the copy the nutrition planning
   * engine and the workout solver use. This wrapper exists because the goal card deliberately
   * differs from that one in two ways: it prefers the most recent measured weight/body-fat pair
   * over the plan's start point, and it discards an implausible answer instead of showing it.
   * Change how estimated body fat works and both wrappers need looking at.
   */
  private getEstimatedBodyFat(estimatedWeight_kg: number): number | undefined {
    let referenceBW_kg: number;
    let referenceBF_pc: number;

    // if user has measured BF with associated weight, use that as reference
    if (this.fitnessState.lastBfDataWeight_kg && this.fitnessState.bfData?.bf_pc) {
      referenceBW_kg = this.fitnessState.lastBfDataWeight_kg;
      referenceBF_pc = this.fitnessState.bfData.bf_pc;
    } else if (this.plan.startPoint?.BF_pc && this.plan.startPoint?.BW_kg) {
      // fall back to start point
      referenceBW_kg = this.plan.startPoint.BW_kg;
      referenceBF_pc = this.plan.startPoint.BF_pc;
    } else {
      // no reference available, use raw value
      return this.fitnessState.bfData?.bf_pc;
    }

    // estimate current BF assuming LBM stayed constant
    const estimatedBF_pc = estimateBFfromConstantLBM(referenceBW_kg, referenceBF_pc, estimatedWeight_kg);

    // sanity check (BF should be between 3% and 60%)
    if (estimatedBF_pc < 3 || estimatedBF_pc > 60) {
      return this.fitnessState.bfData?.bf_pc;
    }

    return roundToPrecision(estimatedBF_pc, 0.1);
  }

  // Add on-track status for GrowthMindset goals
  private async addOnTrackStatusToGoalData() {
    try {
      const checkResult = await this.svPlanHandler.checkForPlanAdjustments();

      if (checkResult) {
        // the same function the Body Composition overview calls, so the two never disagree (#5456)
        const status = progressStatus(checkResult, ProgressStatusRule.Home);
        const isOnTrack = status === ProgressStatus.OnTrack;
        const isIndeterminate = status === ProgressStatus.Undetermined;
        const statusText = isIndeterminate
          ? LL.home.progress.goalCard.undetermined()
          : isOnTrack
            ? LL.home.progress.goalCard.onTrack()
            : LL.home.progress.goalCard.offTrack();

        const recent = isIndeterminate ? this.getRecentAdjustmentBanner(checkResult.message) : null;

        this.goalData.status = {
          text: statusText,
          isOnTrack: isOnTrack,
          isIndeterminate: isIndeterminate,
          indeterminateMessage: isIndeterminate ? checkResult.message : undefined,
          offTrackReason: checkResult.adjustmentReason,
          recentAdjustmentDaysSince: recent?.daysSince,
          daysUntilResume: recent?.daysUntilResume,
          isPlanBaseline: recent?.isPlanBaseline
        };
      }
    } catch (error) {
      // don't show status if we can't determine it. A plan check that could not run is the
      // expected way into here (df-5ba092626ea2) and the service has already reported it under
      // its own stable name, so reporting it again would only split one problem across two
      // Sentry issues. Anything else reaching this catch is a genuine surprise and still goes up.
      //
      // if a database recovery cut one of the check's reads short, the status is skipped this
      // time and nothing is wrong: the page's DocWatcher re-runs this load on the next synced
      // change. Info tier, not abnormal, which would also block the review prompt (#6209).
      if (isDatabaseRecovering(error)) {
        this.logging.trackSilentInfo('Home: goal status skipped while the database recovered', {
          database: error.databaseIdentifier,
        });
        return;
      }
      if (!isPlanCheckFailed(error)) {
        this.logging.trackAbnormalException(error);
      }
    }
  }

  // if the indeterminate state is caused by a recent nutrition adjustment that reset
  // the stats window, surface how long ago it happened and roughly how long until
  // assessment resumes. returns null if this isn't the recent-adjustment case.
  private getRecentAdjustmentBanner(message: PlanUpdateUserMessage | undefined): { daysSince: number; daysUntilResume: number; isPlanBaseline: boolean } | null {
    // only override the "not enough samples post-cutoff" messages; DataErratic and
    // WeightTooFlat are specific and more actionable as-is
    if (message === undefined || !HomePage.RECENT_ADJUSTMENT_MESSAGES.has(message)) {
      return null;
    }
    const adjustedIso = this.plan?.nutritionAdjusted?.timestamp_utc;
    const startPointIso = this.plan?.startPoint?.timestamp_utc;
    if (!adjustedIso || !startPointIso) return null;

    const adjusted = DateTime.fromISO(adjustedIso);
    const startPoint = DateTime.fromISO(startPointIso);
    if (!adjusted.isValid || !startPoint.isValid) return null;

    // if startPoint is newer than nutritionAdjusted the plan was reset; existing copy is safer
    if (adjusted.toMillis() < startPoint.toMillis()) return null;

    const now = this.debug.getNowLocal();
    const adjustedDayLocal = adjusted.setZone(now.zone).startOf('day');
    const nowDay = now.startOf('day');
    const daysSince = Math.max(0, Math.floor(nowDay.diff(adjustedDayLocal, 'days').days));

    // past the 14-day stats window the adjustment is no longer the limiter; fall back
    if (daysSince >= 14) return null;

    const daysUntilResume = Math.max(1, 14 - daysSince);
    // initial intake baseline writes nutritionAdjusted and startPoint together; exact equality is safe
    const isPlanBaseline = adjusted.toMillis() === startPoint.toMillis();

    return { daysSince, daysUntilResume, isPlanBaseline };
  }

  // Determine goal type based on user's declared state and fitness state
  private determineGoalType(declaredState: DeclaredState, immutableState: ImmutableState, fitnessState: FitnessState): GoalType {
    const isBulking = isUsersTrainingGoalGrowth(
      declaredState.trainingGoalGrowth,
      immutableState.gender,
      fitnessState.bfData?.bf_pc ?? 15 // fallback to 15% if no BF data
    );

    // if user has target body fat percentage set
    if (declaredState.targetBodyfat_pc !== undefined && declaredState.targetBodyfat_pc !== null) {
      return GoalType.FatLoss; // Cutting with BF% target
    }

    // if user has target body weight set
    if (declaredState.targetBodyWeight_kg !== undefined && declaredState.targetBodyWeight_kg !== null) {
      if (isBulking) {
        return GoalType.WeightGain; // Bulking with weight target
      } else {
        return GoalType.WeightLoss; // Cutting with weight target
      }
    }

    // no target set - GrowthMindset (indefinite bulk/cut)
    return GoalType.GrowthMindset;
  }

  // Determine if this is a cutting goal (fat loss or weight loss phase)
  private isCuttingGoal(goalType: GoalType): boolean {
    if (goalType === GoalType.FatLoss || goalType === GoalType.WeightLoss) {
      return true;
    }
    if (goalType === GoalType.WeightGain) {
      return false;
    }
    // for GrowthMindset, check the actual training goal
    const isBulking = isUsersTrainingGoalGrowth(
      this.declaredState.trainingGoalGrowth,
      this.immutableState.gender,
      this.fitnessState.bfData?.bf_pc ?? 15
    );
    return !isBulking;
  }

  onCalendarDateSelected(selectedDateTime: DateTime) {
    void this.gotoDateAsync(selectedDateTime.startOf('day'));
  }

  async loadCalendarTaskStatuses(changedRange?: TaskStatusChangeRange) {
    // Calendar day-shading is a best-effort, non-critical UI feature, and this runs
    // fire-and-forget (void) from ionViewWillEnter and the cache-updated subscription. Handle
    // its own errors here so a failure during the boot / initial-sync / session-refresh window
    // can never escape as an unhandled promise rejection (Sentry triage v1.25.0 Group 12,
    // issue 2JB — the dominant identifiable slice came from this exact path).
    try {
      // Initialize calendar task statuses using the optimized cache method
      const today = this.debug.getNowLocal().startOf('day');
      const endDate = today.plus({ days: 7 });
      const startDate = endDate.minus({ months: 1 });

      // skip a reload for a change that is about days this calendar does not
      // show. During a backlog sync most events are: a burst backfilling old log
      // entries publishes an old range, and reloading for it would be pure churn
      // (#4249). No range given means "unknown" — reload, the safe direction.
      //
      // The check reads the whole event, not just its bounding span. The span
      // alone still suppresses plenty — a wave lying entirely in old history
      // fails the overlap test outright. What it cannot suppress is a wave whose
      // days STRADDLE this window with none inside it, and that is what the
      // clusters are for (#4451).
      if (changedRange && !taskStatusChangeAffectsVisibleDays(
        changedRange, startDate.toISODate(), endDate.toISODate())) {
        return;
      }

      // Use the new optimized cache method to get task statuses
      const dayStatuses = await this.userDb.getCachedTaskStatusesAsync(startDate, endDate);

      // Convert DayTaskStatuses to combined TaskStatus for the calendar
      this.calendarTaskStatuses = new Map<string, TaskStatus>();
      dayStatuses.forEach((statuses, date) => {
        this.calendarTaskStatuses.set(date, this.userDb.combineDayTaskStatuses(statuses));
      });

      this.safeMarkForCheck(this.cd);
    } catch (err) {
      // if the data is simply not ready yet — a not-built view (404), or a couch read that
      // 401/403s mid auth-refresh on resume — this is benign: the calendar just isn't shaded
      // yet and self-heals on the next load (TaskStatusCacheUpdatedEvent re-fires this).
      if (isTransientFetchError(err) || isPouchNotFound(err) || isCouchHttpErrorObject(err)) {
        console.warn('Home: calendar task statuses not ready yet (transient), will retry on next load:', err);
        return;
      }
      // genuinely unexpected: surface it instead of swallowing (CLAUDE.md "unexpected
      // conditions must surface"). A persistent failure here is a real bug worth knowing.
      const e = err as { message?: string; name?: string; status?: number };
      this.logging.trackSilentProblem('CalendarTaskStatusLoadFailed', {
        message: e.message,
        name: e.name,
        status: e.status,
      });
    }
  }

  /**
   * Load task statuses for a specific date range
   * Used by the calendar popover to dynamically load statuses for newly visible months
   * Now uses optimized caching to avoid expensive database queries
   */
  async loadTaskStatusesForRange(startDate: DateTime, endDate: DateTime): Promise<Map<string, TaskStatus>> {
    // Use the new cached method which is much faster
    const dayStatuses = await this.userDb.getCachedTaskStatusesAsync(startDate, endDate);

    // Convert DayTaskStatuses to combined TaskStatus for the calendar
    const result = new Map<string, TaskStatus>();
    dayStatuses.forEach((statuses, date) => {
      result.set(date, this.userDb.combineDayTaskStatuses(statuses));
    });
    return result;
  }

  async completeWelcomeFlow() {
    if (this.plan.welcomePhase !== WelcomePhase.Done) {
      this.plan.welcomePhase = WelcomePhase.Done;
      await this.userDb.savePlanAsync(DatabaseWrapper.getTransactionId());
    }
  }

  async handleSettingsButtonClicked(event: Event) {
    const targetElement = event.currentTarget as HTMLElement;

    const buttons: PopupMenuButton[] = [
      {
        text: LL.home.settingsMenu.customizeDashboard(),
        icon: 'settings',
        handler: async () => {
          await this.navCtrl.pushAsync(ICustomizeHomepagePage, {});
        }
      }
    ];

    await this.popupMenuProvider.createMenu({
      buttons,
      targetFromElement: targetElement,
    });
  }

  /**
   * Updates coaching tips based on schedule warnings
   * Selects rotating subset of active warnings (3-5 tips)
   */
  async updateCoachingTips() {
    try {
      const schedule = await this.userDb.loadScheduleAsync();

      // get schedule warnings for exactly one week: today plus the next 6 days.
      // #4687: asking for today plus 7 produced 8 days, and the Schedule Tips
      // page then listed one weekday name twice, because the 8th day falls on
      // the same weekday as the first.
      const today = this.debug.getNowLocal();
      const todayBOD = today.startOf('day');
      const lastDayEOD = today.plus({ days: 6 }).endOf('day');

      this.scheduleWarnings = this.coachingTipsService.getScheduleWarnings(
        schedule,
        todayBOD,
        lastDayEOD
      );

      // get the tips that fired
      const activeWarnings = Object.keys(this.scheduleWarnings) as ScheduleTipId[];

      if (activeWarnings.length === 0) {
        // no warnings, show positive encouragement
        this.coachingTips = [];
        this.showNoTipsMessage = true;
      } else {
        // select random subset of 3-5 warnings
        const tipCount = Math.min(5, Math.max(3, activeWarnings.length));
        const selectedTips: ScheduleTipId[] = [];
        const shuffled = [...activeWarnings].sort(() => Math.random() - 0.5);

        for (let i = 0; i < Math.min(tipCount, shuffled.length); i++) {
          selectedTips.push(shuffled[i]);
        }

        this.coachingTips = selectedTips;
        this.showNoTipsMessage = false;
      }
    } catch (error) {
      // on error, show positive message as fallback
      this.coachingTips = [];
      this.showNoTipsMessage = true;
      this.scheduleWarnings = null;

      // if this is our own teardown, say nothing: the sign-out path shuts every database
      // wrapper down on purpose, and there is no fault to report. `isDatabaseTornDown` is
      // the test rather than `DatabaseRecoveryService.isIntentionalDestruction()` — the helper in
      // database-unavailable-error.ts explains why that flag is still false in this window.
      // It must not be a console.error either, which the E2E unexpected-browser-error gate
      // fails on.
      if (isDatabaseTornDown(error)) {
        console.log('Home: coaching tips skipped, the database is shutting down:', error);
        return;
      }

      // everything else is worth knowing about. The fallback shows the same "your schedule
      // looks good" text a genuinely clean week shows, so a failure is invisible to the user
      // AND to us — and nothing rebuilds the tips until the next entry to this page, so it
      // stays wrong for the rest of the session. Silent rather than abnormal: the tips are
      // not worth interrupting somebody over. The flag separates the transient half of the
      // unavailable family (recovering, singleton not loaded) from a genuine throw.
      this.logging.trackSilentProblem('Home: coaching tips could not be built', {
        databaseUnavailable: isDatabaseUnavailable(error),
        ...errorReportFields(error),
      });
    }
  }

  /**
   * Handles click on "Learn More" link for a specific coaching tip
   * Routes to appropriate page/action based on the tip type
   * @param tip The id of the tip that was clicked; telemetry reports this id, never the tip's translated text
   */
  handleTipLearnMore(tip: ScheduleTipId) {
    // check if this is a schedule warning tip
    if (this.scheduleWarnings && this.scheduleWarnings[tip]) {
      // create filtered warnings object with only the selected tip
      const filteredWarnings: ScheduleWarningsData = {
        [tip]: this.scheduleWarnings[tip]
      };
      window.localStorage.setItem('scheduleTipsMessages', JSON.stringify(filteredWarnings));
      void this.navCtrl.pushAsync(IScheduleTipsPage, {});
      this.telemetry.trackEvent('home_coaching_tip_learn_more', { tip });
      return;
    }

    // future: add handlers for other tip types here
    // example:
    // if (tip.startsWith('nutrition.')) {
    //   this.handleNutritionTip(tip);
    // }
  }

  // fetch chart data for body composition and nutrition charts
  private async fetchChartsDataAsync() {
    // Calculate date range for body composition chart (last 1 month)
    const now = this.debug.getNowLocal();
    const endDate = now.endOf('day');
    const startDate = endDate.minus({ months: 1 }).startOf('month').startOf('day');
    this.bodyCompositionStartDate = startDate.toISODate();
    this.bodyCompositionEndDate = endDate.toISODate();

    // Calculate date range for nutrition chart (last 7 days, excluding today)
    const nutritionEndDate = now.minus({ days: 1 }).endOf('day');
    const nutritionStartDate = nutritionEndDate.minus({ days: 6 }).startOf('day');
    this.nutritionStartDate = nutritionStartDate.toISODate();
    this.nutritionEndDate = nutritionEndDate.toISODate();

    // initialize chart series and axis configs
    this.bodyCompositionSeries = this.bodyCompDataService.getSeries();
    this.bodyCompositionAxisConfig = this.bodyCompDataService.getAxisConfig();
    this.nutritionSeries = this.nutritionDataService.getSeries();
    this.nutritionAxisConfig = this.nutritionDataService.getAxisConfig();

    // the trend line loads beside the dots, so a slow or failed trend never holds them back
    void this.loadBodyCompositionTrendsAsync();

    // fetch body composition data
    this.bodyCompositionData = await this.bodyCompDataService.fetchDataAsync(
      this.bodyCompositionStartDate,
      this.bodyCompositionEndDate,
      this.docWatcher
    );
    if (this.viewDestroyed) return;

    // fetch nutrition data
    this.nutritionData = await this.nutritionDataService.fetchDataAsync(
      this.nutritionStartDate,
      this.nutritionEndDate,
      this.docWatcher
    );
    if (this.viewDestroyed) return;

    this.cd.markForCheck();
  }

  /** the running trend line of the small body composition chart (#6627); none while its switch is off */
  private async loadBodyCompositionTrendsAsync() {
    const loadNumber = ++this.bodyCompositionTrendLoadCounter;
    try {
      const isOn = await isRunningTrendChartsOnAsync(this.configDb);
      if (this.viewDestroyed || loadNumber !== this.bodyCompositionTrendLoadCounter) return;
      if (!isOn || !this.bodyCompositionStartDate || !this.bodyCompositionEndDate) {
        // a new empty array would still count as a change and rebuild the chart, so only clear trends that are there
        if (this.bodyCompositionTrends.length === 0) return;
        this.bodyCompositionTrends = [];
      } else {
        const trends = await this.bodyCompDataService.fetchTrendsAsync({
          firstDayIso: this.bodyCompositionStartDate,
          lastDayIso: this.bodyCompositionEndDate,
          todayIso: this.debug.getNowLocal().toISODate(),
        }, this.docWatcher, false);
        if (this.viewDestroyed || loadNumber !== this.bodyCompositionTrendLoadCounter) return;
        this.bodyCompositionTrends = trends;
      }
    } catch (err) {
      if (this.viewDestroyed || loadNumber !== this.bodyCompositionTrendLoadCounter) return;
      // the chart keeps its dots and lines without a trend; the failure must still reach a debugger
      this.bodyCompositionTrends = [];
      this.logging.trackSilentProblem('homeTrendLoadFailed', errorReportFields(err));
    }
    this.cd.markForCheck();
  }

  private async refreshBodyCompositionDataAsync() {
    this.fitnessState = await this.userDb.loadFitnessStateAsync(this.docWatcher);
    await this.loadGoalDataAsync();
    await this.fetchChartsDataAsync();
  }

  /**
   * Callback for meal suggestion result from MealSuggestionsPage.
   * Forwards to the active cs-day-items component.
   */
  async onMealSuggestionResult(context: { mealId: string }, suggestion: MealTunerMeal | undefined) {
    await this.dayItemsComponent?.onMealSuggestionResult(context, suggestion);
  }

  // forwards meal photo scan result to cs-day-items
  async onMealPhotoScanResult(context: { mealId: string }, result: MealPhotoScannerResult) {
    await this.dayItemsComponent?.onMealPhotoScanResult(context, result);
  }

  // forwards the meal photo scanner's product-label redirect food pick to cs-day-items (#3902)
  async onMealScanRedirectFood(context: { mealId: string }, result: FoodsPageResult) {
    await this.dayItemsComponent?.onMealScanRedirectFood(context, result);
  }
}
